import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import Card from "~/components/Card";
import SearchInput from "~/components/formElements/SearchInput";
import Table from "~/components/Table";
import { PENDING_INVOICES_INDEX_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import type { PendingInvoicesIndexResponse } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import DeliveryPeriodFilter from "~/modules/billing/components/DeliveryPeriodFilter";
import {
	applyDeliveryPeriodFilter,
	castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import InvoiceClientFilter from "~/modules/billing/components/InvoicesIndexView/components/InvoiceClientFilter.tsx";
import DownloadPendingInvoicesTableDataButton
	from "~/modules/billing/components/PendingInvoicesIndexView/components/DownloadPendingInvoicesTableDataButton/DownloadPendingInvoicesTableDataButton.tsx";
import StartBillingProcessForProjectInvoicesSidebar
	from "~/modules/billing/components/PendingInvoicesIndexView/components/StartBillingProcessForProjectInvoicesSidebar/StartBillingProcessForProjectInvoicesSidebar.tsx";
import UpdatePendingInvoiceCommentSidebar
	from "~/modules/billing/components/PendingInvoicesIndexView/components/UpdatePendingInvoiceCommentSidebar";
import {
	pendingInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/PendingInvoicesIndexView/pendingInvoicesTableColumnDefs.tsx";
import type {
	InvoiceCommentData,
	PendingInvoicesIndexTableData,
	UnprocessedInvoicesIndexTableMeta,
} from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { createPendingInvoiceTableDataFromProjectInvoice } from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import type { ColumnFilterType } from "~/types/tanstackTableTypes.ts";
import {
	createColumnFilterInputHandler,
	getColumnFilterValueForKey,
	sanitiseColumnFilters,
} from "~/utils/tanStackTableUtils.ts";

import PendingInvoicesIndexActiveFilters from "./components/PendingInvoicesIndexActiveFilters";

type Props = {
	isLoading: boolean,
	pendingInvoicesData?: PendingInvoicesIndexResponse
};

type DefaultPageStateType = {
	columnFilters: ColumnFilterType[];
	deliveryPeriodFilter: DateRangeNullable;
	globalFilter: string;
	sorting: SortingState;
};

const defaultPageState: DefaultPageStateType = {
	columnFilters: [
		{ id: "companyDisplayName", value: [] },
	],
	deliveryPeriodFilter: { startDate: null, endDate: null },
	globalFilter: "",
	sorting: [{ id: "deliveryStartDate", desc: true }],
};


const PendingInvoicesIndexView = ({ isLoading, pendingInvoicesData }: Props) => {
	const { hasAnyPermission } = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: PENDING_INVOICES_INDEX_PAGE_STATE_KEY,
		defaultState: defaultPageState,
	});
	const [invoiceCommentToUpdate, setInvoiceCommentToUpdate] = useState<InvoiceCommentData | undefined>();
	const [invoiceIdToShowDetailsOf, setInvoiceIdToShowDetailsOf] = useState<string | undefined>();
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(defaultPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(defaultPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);
	const [columnFilters, setColumnFilters] = useState<ColumnFilterType[]>(
		sanitiseColumnFilters(defaultPageState.columnFilters, pageState.columnFilters));

	useEffect(() => {
		setPageState({ globalFilter, deliveryPeriodFilter, sorting, columnFilters });
	}, [columnFilters, deliveryPeriodFilter, globalFilter, setPageState, sorting]);

	const handleColumnFilterInputChange = createColumnFilterInputHandler(columnFilters, setColumnFilters);

	const tableData: PendingInvoicesIndexTableData[] = useMemo(() => {
		if (pendingInvoicesData) {
			return pendingInvoicesData.projectInvoices.map((invoice) => {
				return createPendingInvoiceTableDataFromProjectInvoice(invoice, pendingInvoicesData.projectPhases, pendingInvoicesData.projects);
			});
		}
		return [];
	}, [pendingInvoicesData]);

	const preFilteredTableData = useMemo(() => {
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
	}, [tableData, deliveryPeriodFilter]);

	const handleRowClick = useCallback((row: Row<PendingInvoicesIndexTableData>) => {
		setInvoiceIdToShowDetailsOf(row.original.id);
	}, []);

	const handleUpdateCommentClick = useCallback((event: MouseEvent, row: Row<PendingInvoicesIndexTableData>) => {
		event.stopPropagation();
		setInvoiceCommentToUpdate({
			invoiceId: row.original.id,
			comment: row.original.comment,
			isProjectInvoice: row.original.isProjectInvoice,
		});
	}, []);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: pendingInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			columnFilters,
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
			onUpdateCommentClick: handleUpdateCommentClick,
		} as UnprocessedInvoicesIndexTableMeta,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	return <div className={clsx("auto-grid-3-rows h-screen ")}>
		<InvoicePageHeader currentPage="pending"
						   userCanManageInvoices={userCanManageInvoices}
		/>
		<div className="z-50 mb-2 w-full px-8">
			<Card shadow="sm"
				  padding="sm">
				<div className={"flex w-full items-center gap-x-4"}>
					<div className="w-64">
						<SearchInput value={globalFilter}
									 inputSize="sm"
									 autoFocus={true}
									 onChange={setGlobalFilter} />

					</div>
					<div className="w-64">
						<DeliveryPeriodFilter onChange={setDeliveryPeriodFilter}
											  inputSize="sm"
											  selectedPeriod={deliveryPeriodFilter} />
					</div>

					<div className="w-64">
						<InvoiceClientFilter onChange={(value) => handleColumnFilterInputChange("companyDisplayName", value)}
											 filterValues={getColumnFilterValueForKey("companyDisplayName", columnFilters)} />
					</div>
					<div className="ml-auto">
						<DownloadPendingInvoicesTableDataButton deliveryPeriodFilter={deliveryPeriodFilter}
																filteredTableData={table.getFilteredRowModel().rows.map(row => row.original)}
																search={globalFilter} />
					</div>
				</div>
			</Card>
			<PendingInvoicesIndexActiveFilters clientFilterValue={getColumnFilterValueForKey("companyDisplayName", columnFilters)}
											   onChangeClientFilter={(value) => handleColumnFilterInputChange("companyDisplayName", value)}
			/>
		</div>
		<div className="size-full space-y-8 overflow-y-auto px-8 pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true} />
		</div>
		<StartBillingProcessForProjectInvoicesSidebar invoiceId={invoiceIdToShowDetailsOf}
													  isOpen={!!invoiceIdToShowDetailsOf}
													  onClose={() => setInvoiceIdToShowDetailsOf(undefined)}
													  userCanManageInvoices={userCanManageInvoices}
		/>
		<UpdatePendingInvoiceCommentSidebar isOpen={!!invoiceCommentToUpdate}
											onClose={() => setInvoiceCommentToUpdate(undefined)}
											invoiceCommentData={invoiceCommentToUpdate}
											userCanManageInvoices={userCanManageInvoices}
		/>
	</div>;
};

export default PendingInvoicesIndexView;